<template>
  <DialogTemplateToolkit
    class-name="new-tender-stage"
    :dialog="showModal"
    :disabled="loading"
    first_btn="Tenders.Continue"
    :loading="loading"
    :second_btn="blockCopy ? 'Common.Close' : ''"
    :title="$t('Tenders.NewStage')"
    :without_first_btn="blockCopy && copy_offers === 'SELECTED'"
    @apply="submit"
    @close="close">
    <template #activator-btn>
      <sButton
        data-qa="new-tender-stage-btn"
        type="border"
        @click="showModal = true">
        {{ $t('Tenders.NewStage') }}
      </sButton>
    </template>
    <template #card-text>
      <sForm
        ref="new-tender-stage-form"
        :disabled="loading"
        label-position="top"
        :model="form"
        :rules="rules">
        <sFormItem
          prop="text">
          <template v-slot:label>
            <span class="h8-text px-0">
              {{ $t('Tenders.NewTenderStageReasonLabel') }}
            </span>
          </template>
          <sInput
            data-qa="new-tender-stage-reason"
            max-rows="10"
            rows="5"
            show-word-limit
            type="textarea"
            :value.sync="form.text"/>
        </sFormItem>
        <CopyOffersSection
          v-if="can_select_bids"
          :attachments_required="$_mustCopyAttachments"
          :block_copy="blockCopy"
          :copy_offers.sync="copy_offers"
          :copy_offers_files.sync="copy_offers_files"
          :disabled="loading"
          :not_possible_message="$t('Tenders.CreateNewStageNotPossible')"
          :text="$t('Tenders.CopyBidsToNewStageText')"
          :title="$t('Tenders.CopyBidsToNewStage')"
          title_class="pt-4"/>
      </sForm>
    </template>
    <template
      v-if="copy_offers === 'SELECTED' && !blockCopy"
      #first_btn>
      <SelectOffersForCopy
        :copy-tender="createNewStage"
        :copy_offers_files="copy_offers_files"
        :disabled="loading"
        :loading="loading"
        :tender="tender"
        :validate="validate"/>
    </template>
  </DialogTemplateToolkit>
</template>
<script>
import SelectOffersForCopy from 'pages/tender creation/SelectOffersForCopy.vue'
import CopyOffersSection from 'components/tenders/dialogs/CopyOffersSection.vue'
import DialogTemplateToolkit from 'components/common/dialogs/DialogTemplateToolkit.vue'

import { validateRuls, copyTenderMixin } from 'mixins'
import { COPY_OFFERS_TYPES } from 'mixins/copyTenderMixin'
import SmarttenderApi from '@/api/smarttenderApi'

const st_api = new SmarttenderApi()

/**
 * Кнопка + модальна форма "Новий етап" на панелі сторінки тендеру.
 * Форма (крок 1) і виклик мутації/навігація (крок 2) — ANALYSIS-DESIGN Д3+Д5,
 * реалізовані в цьому самому файлі (design-документ вбудовує Крок 5 у Крок 3).
 * Крок вибору конкретних пропозицій (Д4) — коли copy_offers === 'SELECTED',
 * підключає наявний SelectOffersForCopy.vue без змін (той самий компонент,
 * що CopyTender.vue), передаючи createNewStage як callback copy-tender.
 */
export default {
  name: 'NewTenderStageBtn',
  mixins: [ validateRuls, copyTenderMixin ],
  data() {
    const that = this
    return {
      loading: false,
      showModal: false,
      form: {
        text: ''
      },
      rules: {
        text: [
          {
            type: 'string',
            trigger: 'change',
            transform(value) {
              return value.trim()
            },
            required: true,
            min: 2,
            message: this.$t('Validate.minLength', {number: 2, type: this.$tc('Common.characters', 2) })
          },
          {
            type: 'string',
            required: true,
            trigger: 'change',
            transform(value) {
              return value?.trim()
            },
            max: 4000,
            message: that.$t('Validate.maxLength', {number: 4000, type: that.$tc('Common.characters', 10) })
          }
        ]
      }
    }
  },
  props: {
    tender: Object
  },
  computed: {
    can_select_bids() {
      if (this.tender.biddingType?.dontCopySuppliers) {
        return false
      }

      if (!this.tender.bids?.length) {
        return false
      }

      const status = this.tender?.tenderStatus?.id || this.tender?.tenderStatusId
      return [this.statuseKeys.BID_ANALYSIS].includes(status) && this.$can('copy', 'bids')
    },
  },
  methods: {
    close() {
      this.showModal = false
      this.copy_offers = COPY_OFFERS_TYPES.NONE
      this.copy_offers_files = true
      this.form.text = ''
    },
    async validate() {
      return await this.$refs['new-tender-stage-form'].validate()
    },
    submit() {
      if (this.blockCopy) {
        return
      }
      this.validate()
        .then(() => this.createNewStage())
    },
    async createNewStage(bids = []) {
      this.loading = true
      this.$root.$emit('showOverlay')

      const tender = this.$_.cloneDeep(this.tender)

      // Підготовка активних пропозицій до копіювання, коли обрано "копіювати всі" — та сама логіка, що в CopyTender.copyTender()
      bids = await this.$_resolveBidsForCopy(tender, bids)

      const model = {
        tenderId: tender.tenderId,
        biddingTypeId: tender.biddingType?.biddingTypeId || tender.biddingTypeId,
        organizationItId: tender.organizer?.organizationItId || this.$route.params.org,
        bids: this.$_mapBidsToCopyModel(bids),
        reason: this.form.text
      }

      try {
        const newTenderId = await st_api.createNewTenderStage(model)

        if (!newTenderId) {
          throw this.$t('Common.ErrorTitle')
        }

        this.showModal = false

        // Перехід до редагування нового тендеру-етапу — той самий референс, що CopyTender.copyTender();
        // без changeOrganisation-кроку — organizationItId завжди дорівнює поточній організації (Field semantic trace, ANALYSIS-DESIGN)
        this.$nextTick(() => {
          this.$store.commit('setNewTenderInfo', { mode: 'edit', step: 1 })
          this.$router.push({
            name: 'editTender',
            query: { step: 1, id: newTenderId },
            params: { org: model.organizationItId }
          })
        })
      } catch (error) {
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          message: this.$_getCopyErrorMessage(error),
          duration: 10,
        })
        this.$log_error(error)
        // модалка НЕ закривається (EC-1/EC-3) — користувач бачить помилку, форма лишається відкритою
      } finally {
        this.loading = false
        this.$root.$emit('closeOverlay')
      }
    },
  },
  created() {
    this.checkFormula()
  },
  components: {
    SelectOffersForCopy,
    CopyOffersSection,
    DialogTemplateToolkit
  }
}
</script>
<style lang="scss" scoped>
.new-tender-stage {
  .h2-headline {
    line-height: 28px !important;
  }
}
</style>
